import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

interface QueuedMessage {
  id: string;
  content: string;
  options?: Record<string, any>;
  timestamp: number;
  status: 'pending' | 'sending' | 'failed';
  retryCount: number;
}

const STORAGE_KEY = 'geniebot-offline-queue';
const MAX_RETRIES = 3;
const OFFLINE_TOAST_ID = 'offline-status-toast';

// Singleton for global offline state
let globalOnlineState = typeof navigator !== 'undefined' ? navigator.onLine : true;
const listeners = new Set<(online: boolean) => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    globalOnlineState = true;
    listeners.forEach(fn => fn(true));
  });
  window.addEventListener('offline', () => {
    globalOnlineState = false;
    listeners.forEach(fn => fn(false));
  });
}

export function isCurrentlyOnline(): boolean {
  return globalOnlineState;
}

export function subscribeToOnlineStatus(callback: (online: boolean) => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

export const useOfflineQueue = () => {
  const [queue, setQueue] = useState<QueuedMessage[]>([]);
  const [isOnline, setIsOnline] = useState(isCurrentlyOnline());
  const [isSyncing, setIsSyncing] = useState(false);

  // Load queue from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        setQueue(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse offline queue:', e);
      }
    }
  }, []);

  // Save queue to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  }, [queue]);

  // Listen for online/offline events with toast notifications
  useEffect(() => {
    let wasOffline = !navigator.onLine;
    
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        toast.success('Back online!', { id: OFFLINE_TOAST_ID });
        wasOffline = false;
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      wasOffline = true;
      toast.warning('You are offline. Messages will be queued.', { 
        id: OFFLINE_TOAST_ID,
        duration: Infinity,
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial state
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync queue when coming online
  useEffect(() => {
    if (isOnline && queue.length > 0 && !isSyncing) {
      syncQueue();
    }
  }, [isOnline, queue.length]);

  const addToQueue = useCallback((content: string, options?: Record<string, any>) => {
    const newMessage: QueuedMessage = {
      id: crypto.randomUUID(),
      content,
      options,
      timestamp: Date.now(),
      status: 'pending',
      retryCount: 0,
    };

    setQueue(prev => [...prev, newMessage]);
    toast.info('Message queued for when you\'re back online');
    return newMessage.id;
  }, []);

  const removeFromQueue = useCallback((id: string) => {
    setQueue(prev => prev.filter(m => m.id !== id));
  }, []);

  const syncQueue = useCallback(async () => {
    if (isSyncing || !isOnline) return;

    setIsSyncing(true);
    const pendingMessages = queue.filter(m => m.status === 'pending' || m.status === 'failed');

    if (pendingMessages.length > 0) {
      toast.info(`Processing ${pendingMessages.length} queued message(s)...`);
    }

    for (const message of pendingMessages) {
      if (message.retryCount >= MAX_RETRIES) {
        // Remove permanently failed messages
        removeFromQueue(message.id);
        continue;
      }

      setQueue(prev => prev.map(m => 
        m.id === message.id ? { ...m, status: 'sending' } : m
      ));

      try {
        // This would integrate with your actual send function
        // For now, we just remove it from the queue on success
        removeFromQueue(message.id);
      } catch (error) {
        setQueue(prev => prev.map(m => 
          m.id === message.id ? { ...m, status: 'failed', retryCount: m.retryCount + 1 } : m
        ));
      }
    }

    setIsSyncing(false);
  }, [isSyncing, isOnline, queue, removeFromQueue]);

  const clearQueue = useCallback(() => {
    setQueue([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    queue,
    isOnline,
    isSyncing,
    pendingCount: queue.filter(m => m.status === 'pending').length,
    addToQueue,
    removeFromQueue,
    syncQueue,
    clearQueue,
  };
};
